import React, { useMemo, useState } from "react";

import { Title } from "../../component/TitleUtils";
import { InputField } from "../../component/FormUtils";
import { MyMessage } from "../../component/messageUtils";
import { MyActionButton } from "../../component/ActionButtonUtils";
import { useAdminContext } from "../../../../context/admin/context/AdminContext";

import {
  FiLock,
  FiEye,
  FiEyeOff,
  FiShield,
  FiCheckCircle,
  FiXCircle,
  FiInfo,
} from "react-icons/fi";

const Overview = () => {
  const { updateAdminPassword, logOut } = useAdminContext();

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [password, setPassword] = useState(null);
  const [confirmPassword, setConfirmPassword] = useState(null);

  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  const responses = (isError, msg) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(msg);
  };

  const addOnePartnerHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!password || !confirmPassword) {
      setLoading(false);
      return responses(true, "Password Required");
    } else if (password !== confirmPassword) {
      setLoading(false);
      return responses(true, "Password should match");
    } else {
      await sendDBRequest();
    }
  };

  /**
   * @author Ntavigwa Bashombe
   */
  const sendDBRequest = async () => {
    const { status } = await updateAdminPassword({ password });
    if (status === 200) {
      responses(false, message);
      setTimeout(() => logOut(), 1000);
    } else {
      setLoading(false);
      responses(true, message);
    }
  };

  const pwd = password ?? "";
  const confirmPwd = confirmPassword ?? "";

  const strength = useMemo(() => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    score = Math.min(score, 4);

    const labels = ["Very weak", "Weak", "Okay", "Good", "Strong"];
    const percentage = (score / 4) * 100;

    return {
      score,
      label: labels[score],
      percentage,
      checks: {
        length: pwd.length >= 8,
        upper: /[A-Z]/.test(pwd),
        lower: /[a-z]/.test(pwd),
        number: /\d/.test(pwd),
        symbol: /[^A-Za-z0-9]/.test(pwd),
      },
    };
  }, [pwd]);

  const matches = useMemo(() => {
    if (!pwd && !confirmPwd) return null;
    return pwd === confirmPwd;
  }, [pwd, confirmPwd]);

  const meterBarClass = useMemo(() => {
    if (strength.score <= 1) return "bg-red-400";
    if (strength.score === 2) return "bg-yellow-400";
    if (strength.score === 3) return "bg-green-400";
    return "bg-emerald-500";
  }, [strength.score]);

  const labelTone = {
    neutral:
      "bg-gray-100 text-gray-700 border border-gray-200",
    positive:
      "bg-emerald-50 text-emerald-700 border border-emerald-200",
  };

  return (
    <div className="h-full overflow-y-auto rounded-b-xl bg-gradient-to-b from-white to-gray-50">
      <form onSubmit={addOnePartnerHandler} className="max-w-3xl mx-auto p-6 md:p-8">
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-gray-900 text-white">
              <FiLock className="h-4 w-4" />
            </span>
            <Title props={{ title: "Update Your Password" }} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${labelTone.neutral}`}>
              <FiInfo className="h-3.5 w-3.5" />
              You’ll be logged out after a successful update
            </span>
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${labelTone.positive}`}>
              <FiShield className="h-3.5 w-3.5" />
              Security best practice
            </span>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="p-6 md:p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Password */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-[13px] font-semibold text-gray-800">
                    New Password
                  </label>
                  <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-[2px] text-[10px] font-semibold text-gray-700">
                    <FiShield className="h-3 w-3" />
                    Keep it strong
                  </span>
                </div>

                <InputField
                  props={{
                    id: "password",
                    title: "",
                    type: showPwd ? "text" : "password",
                    placeholder: "Password",
                    // required: true,
                    value: password ? password : "",
                    onChange: (e) => {
                      setPassword(e.target.value);
                      setError(null);
                      setSuccess(null);
                    },
                  }}
                />

                {/* Toggle */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-800 transition"
                  >
                    {showPwd ? <FiEyeOff className="h-3.5 w-3.5" /> : <FiEye className="h-3.5 w-3.5" />}
                    {showPwd ? "Hide" : "Show"}
                  </button>
                </div>

                {/* Strength meter */}
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-gray-700">Strength</span>
                    <span
                      className={[
                        "inline-flex items-center gap-1 rounded-md px-2 py-[2px] font-semibold",
                        strength.score >= 3
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-amber-50 text-amber-700 border border-amber-200",
                      ].join(" ")}
                    >
                      {strength.score >= 3 ? (
                        <FiCheckCircle className="h-3.5 w-3.5" />
                      ) : (
                        <FiInfo className="h-3.5 w-3.5" />
                      )}
                      {strength.label}
                    </span>
                  </div>
                  <div className="mt-1 h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className={`h-2 ${meterBarClass} transition-all`}
                      style={{ width: `${strength.percentage}%` }}
                      aria-hidden
                    />
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <CheckItem ok={strength.checks.length} label="8+ characters" />
                    <CheckItem ok={strength.checks.number} label="Number" />
                    <CheckItem ok={strength.checks.upper} label="Uppercase" />
                    <CheckItem ok={strength.checks.symbol} label="Symbol" />
                  </div>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="confirmPassword" className="text-[13px] font-semibold text-gray-800">
                    Confirm Password
                  </label>
                  <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-[2px] text-[10px] font-semibold text-gray-700">
                    <FiLock className="h-3 w-3" />
                    Must match
                  </span>
                </div>

                <InputField
                  props={{
                    id: "confirmPassword",
                    title: "",
                    type: showConfirmPwd ? "text" : "password",
                    placeholder: "Confirm Password",
                    // required: true,
                    value: confirmPassword ? confirmPassword : "",
                    onChange: (e) => {
                      setConfirmPassword(e.target.value);
                      setError(null);
                      setSuccess(null);
                    },
                  }}
                />

                {/* Toggle */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowConfirmPwd((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-800 transition"
                  >
                    {showConfirmPwd ? <FiEyeOff className="h-3.5 w-3.5" /> : <FiEye className="h-3.5 w-3.5" />}
                    {showConfirmPwd ? "Hide" : "Show"}
                  </button>
                </div>

                {/* Match indicator */}
                {matches !== null && (
                  <p
                    className={`mt-1 inline-flex items-center gap-1 text-xs font-semibold ${matches ? "text-emerald-700" : "text-red-600"
                      }`}
                    role="status"
                    aria-live="polite"
                  >
                    {matches ? (
                      <FiCheckCircle className="h-3.5 w-3.5" />
                    ) : (
                      <FiXCircle className="h-3.5 w-3.5" />
                    )}
                    {matches ? "Passwords match" : "Passwords do not match"}
                  </p>
                )}
              </div>
            </div>

            <div className="border-t border-gray-100" />

            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <FiInfo className="h-3.5 w-3.5" />
                <span>Use words, numbers & symbols for better security.</span>
              </div>

              <div className="flex items-center gap-3">
                <MyActionButton
                  loading={loading}
                  title="Save Changes"
                  onClick={() => {
                    setError(false);
                    setSuccess(false);
                    setErrorMessage(null);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

const CheckItem = ({ ok, label }) => (
  <span
    className={[
      "inline-flex items-center gap-2",
      ok ? "text-emerald-700" : "text-gray-500",
    ].join(" ")}
  >
    {ok ? (
      <FiCheckCircle className="h-3.5 w-3.5" />
    ) : (
      <span className="inline-block h-2 w-2 rounded-full bg-current" />
    )}
    {label}
  </span>
);

export default Overview;
